<template>
  <div class="rise-in">
    <!-- Header -->
    <div
      class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8"
    >
      <div>
        <h1 class="page-title">User management</h1>
        <p class="page-subtitle">
          Create accounts, control access, and assign projects.
        </p>
      </div>
      <button
        class="btn btn-primary btn-md self-start sm:self-auto"
        @click="showCreate = !showCreate"
      >
        <font-awesome-icon :icon="showCreate ? 'times' : 'plus'" />
        {{ showCreate ? 'Cancel' : 'New user' }}
      </button>
    </div>

    <!-- Notices -->
    <div
      v-if="notice"
      class="flex items-start gap-3 p-4 rounded-xl bg-green-50 border border-green-100 mb-6"
    >
      <font-awesome-icon icon="check-circle" class="text-green-600 mt-0.5" />
      <p class="text-sm text-ink">{{ notice }}</p>
    </div>
    <div
      v-if="error"
      class="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100 mb-6"
    >
      <font-awesome-icon
        icon="exclamation-triangle"
        class="text-red-600 mt-0.5"
      />
      <p class="text-sm text-ink">{{ error }}</p>
    </div>

    <!-- Create user panel -->
    <div v-if="showCreate" class="card card-pad mb-8">
      <h3 class="section-title mb-4">Create a new user</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormulateInput
          v-model="form.email"
          type="email"
          label="Email"
          placeholder="name@example.com"
        />
        <FormulateInput
          v-model="form.password"
          type="password"
          label="Initial password"
          placeholder="At least 8 characters"
          help="The user can change this later under Settings."
        />
        <FormulateInput
          v-model="form.role"
          type="select"
          label="Role"
          :options="{ operator: 'Operator', admin: 'Admin' }"
        />
      </div>

      <div class="mt-4">
        <p class="text-sm font-semibold text-ink mb-2">Project access</p>
        <div class="flex flex-wrap gap-2">
          <label
            v-for="p in allProjects"
            :key="p.id"
            class="cursor-pointer"
            @click.prevent="toggleFormProject(p.id)"
          >
            <span
              class="pill"
              :class="
                form.projectIds.includes(p.id) ? 'pill-brand' : 'pill-muted'
              "
            >
              <font-awesome-icon
                :icon="form.projectIds.includes(p.id) ? 'check' : 'plus'"
                class="text-xs"
              />
              {{ projectLabel(p) }}
            </span>
          </label>
        </div>
      </div>

      <div class="mt-5 flex gap-3">
        <button
          class="btn btn-primary btn-md"
          :disabled="creating"
          @click="createUser"
        >
          <font-awesome-icon v-if="creating" icon="circle-notch" spin />
          {{ creating ? 'Creating…' : 'Create user' }}
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="loading"
      class="flex items-center gap-2 text-ink-muted py-12 justify-center"
    >
      <font-awesome-icon icon="circle-notch" spin class="text-brand-600" />
      Loading users…
    </div>

    <!-- Users -->
    <div v-else class="space-y-4">
      <div v-for="u in users" :key="u.id" class="card card-pad">
        <div
          class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4"
        >
          <!-- Identity -->
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="font-bold text-ink break-all">{{ u.email }}</span>
              <span v-if="u.id === myUid" class="pill pill-muted">You</span>
              <span
                class="pill"
                :class="u.role === 'admin' ? 'pill-brand' : 'pill-muted'"
              >
                {{ u.role }}
              </span>
              <span
                class="pill"
                :class="u.active ? 'pill-green' : 'pill-amber'"
              >
                {{ u.active ? 'Active' : 'Disabled' }}
              </span>
              <span
                v-if="u.must_change_password"
                class="pill pill-amber"
                title="User still has the admin-set temporary password"
              >
                <font-awesome-icon icon="key" class="text-xs" />
                Temp password
              </span>
            </div>

            <!-- Project access -->
            <div class="mt-3">
              <p
                class="text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2"
              >
                Project access
              </p>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="p in allProjects"
                  :key="p.id"
                  type="button"
                  @click="toggleProject(u, p.id)"
                >
                  <span
                    class="pill"
                    :class="
                      u.draftProjects.includes(p.id)
                        ? 'pill-brand'
                        : 'pill-muted'
                    "
                  >
                    <font-awesome-icon
                      :icon="u.draftProjects.includes(p.id) ? 'check' : 'plus'"
                      class="text-xs"
                    />
                    {{ projectLabel(p) }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Role selector (inline) -->
            <div class="mt-3 flex items-center gap-2">
              <span
                class="text-xs font-semibold uppercase tracking-wider text-ink-muted"
                >Role</span
              >
              <select
                v-model="u.draftRole"
                class="text-sm border border-gray-200 rounded-lg px-2 py-1 bg-white"
                :disabled="u.id === myUid"
              >
                <option value="operator">Operator</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <!-- Save changes -->
            <div v-if="isDirty(u)" class="mt-3">
              <button
                class="btn btn-primary btn-sm"
                :disabled="busyId === u.id"
                @click="saveUser(u)"
              >
                <font-awesome-icon
                  v-if="busyId === u.id"
                  icon="circle-notch"
                  spin
                />
                Save changes
              </button>
              <button class="btn btn-ghost btn-sm" @click="resetDraft(u)">
                Discard
              </button>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex flex-wrap gap-2 lg:justify-end lg:flex-shrink-0">
            <button
              class="btn btn-secondary btn-sm"
              :disabled="busyId === u.id || (u.id === myUid && u.active)"
              @click="toggleActive(u)"
            >
              <font-awesome-icon :icon="u.active ? 'user-slash' : 'user'" />
              {{ u.active ? 'Disable' : 'Enable' }}
            </button>
            <button class="btn btn-secondary btn-sm" @click="startPwReset(u)">
              <font-awesome-icon icon="key" />
              Reset password
            </button>
            <button
              class="btn btn-danger btn-sm"
              :disabled="busyId === u.id || u.id === myUid"
              @click="removeUser(u)"
            >
              <font-awesome-icon icon="trash" />
              Delete
            </button>
          </div>
        </div>

        <!-- Inline password reset -->
        <div
          v-if="pwFor === u.id"
          class="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-end gap-3"
        >
          <div class="flex-1 min-w-40">
            <label
              class="text-xs font-semibold uppercase tracking-wider text-ink-muted"
              >New password</label
            >
            <input
              v-model="pwValue"
              type="text"
              placeholder="At least 8 characters"
              class="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 bg-white text-sm"
            />
          </div>
          <button
            class="btn btn-primary btn-sm"
            :disabled="busyId === u.id || pwValue.length < 8"
            @click="confirmPwReset(u)"
          >
            Set password
          </button>
          <button class="btn btn-ghost btn-sm" @click="pwFor = null">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { admin, rest } from 'helpers/api'
import { getterTypes } from 'store/auth'

const sorted = a => [...a].sort().join(',')

export default {
  name: 'UserManagement',
  data() {
    return {
      loading: true,
      creating: false,
      busyId: null,
      error: null,
      notice: null,
      users: [],
      allProjects: [],
      showCreate: false,
      form: { email: '', password: '', role: 'operator', projectIds: [] },
      pwFor: null,
      pwValue: '',
    }
  },
  computed: {
    ...mapGetters({ currentUser: getterTypes.currentUser }),
    myUid() {
      return this.currentUser && this.currentUser.uid
    },
  },
  created() {
    this.load()
  },
  methods: {
    projectLabel(p) {
      return (p.name || p.id || '').toString().toUpperCase()
    },
    async load() {
      this.loading = true
      this.error = null
      try {
        const [users, projects] = await Promise.all([
          admin.listUsers(),
          rest.listProjects(),
        ])
        this.allProjects = projects
        this.users = users.map(u => ({
          ...u,
          draftRole: u.role,
          draftProjects: [...(u.projectIds || [])],
        }))
      } catch (e) {
        this.error = this.msg(e, 'Could not load users.')
      } finally {
        this.loading = false
      }
    },
    isDirty(u) {
      return (
        u.draftRole !== u.role ||
        sorted(u.draftProjects) !== sorted(u.projectIds)
      )
    },
    resetDraft(u) {
      u.draftRole = u.role
      u.draftProjects = [...u.projectIds]
    },
    toggleProject(u, pid) {
      const i = u.draftProjects.indexOf(pid)
      if (i === -1) u.draftProjects.push(pid)
      else u.draftProjects.splice(i, 1)
    },
    toggleFormProject(pid) {
      const i = this.form.projectIds.indexOf(pid)
      if (i === -1) this.form.projectIds.push(pid)
      else this.form.projectIds.splice(i, 1)
    },
    async saveUser(u) {
      this.busyId = u.id
      this.error = this.notice = null
      try {
        await admin.updateUser(u.id, {
          role: u.draftRole,
          projectIds: u.draftProjects,
        })
        u.role = u.draftRole
        u.projectIds = [...u.draftProjects]
        this.notice = `Updated ${u.email}.`
      } catch (e) {
        this.error = this.msg(e, 'Could not save changes.')
      } finally {
        this.busyId = null
      }
    },
    async toggleActive(u) {
      this.busyId = u.id
      this.error = this.notice = null
      try {
        await admin.updateUser(u.id, { active: !u.active })
        u.active = !u.active
        this.notice = `${u.email} is now ${u.active ? 'active' : 'disabled'}.`
      } catch (e) {
        this.error = this.msg(e, 'Could not change account status.')
      } finally {
        this.busyId = null
      }
    },
    startPwReset(u) {
      this.pwFor = u.id
      this.pwValue = ''
    },
    async confirmPwReset(u) {
      this.busyId = u.id
      this.error = this.notice = null
      try {
        await admin.setPassword(u.id, this.pwValue)
        this.notice = `Password reset for ${u.email}.`
        this.pwFor = null
        this.pwValue = ''
      } catch (e) {
        this.error = this.msg(e, 'Could not reset password.')
      } finally {
        this.busyId = null
      }
    },
    async removeUser(u) {
      if (
        !window.confirm(
          `Delete ${u.email}? This permanently removes the account and cannot be undone.`
        )
      )
        return
      this.busyId = u.id
      this.error = this.notice = null
      try {
        await admin.deleteUser(u.id)
        this.users = this.users.filter(x => x.id !== u.id)
        this.notice = `Deleted ${u.email}.`
      } catch (e) {
        this.error = this.msg(e, 'Could not delete user.')
      } finally {
        this.busyId = null
      }
    },
    async createUser() {
      this.creating = true
      this.error = this.notice = null
      try {
        await admin.createUser({
          email: this.form.email,
          password: this.form.password,
          role: this.form.role,
          projectIds: this.form.projectIds,
        })
        this.notice = `Created ${this.form.email}.`
        this.form = {
          email: '',
          password: '',
          role: 'operator',
          projectIds: [],
        }
        this.showCreate = false
        await this.load()
      } catch (e) {
        this.error = this.msg(e, 'Could not create user.')
      } finally {
        this.creating = false
      }
    },
    msg(e, fallback) {
      return (
        (e.response &&
          e.response.data &&
          (e.response.data.error ||
            e.response.data.message ||
            (e.response.data.error && e.response.data.error.message))) ||
        e.message ||
        fallback
      )
    },
  },
}
</script>
